import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Archive, CheckCircle2, FileText, Plus, Trash2, Upload } from 'lucide-react';
import { formatDate, formatMoney, type Paginated } from '@erp/shared';
import { api, ApiError, errorMessage, openPdf } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDebounced } from '@/hooks/useDebounced';
import { PageHeader, SearchInput, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { toast } from 'sonner';
import { InvoiceTemplateEditor } from './InvoiceTemplateEditor';
import { PaymentsPanel } from './PaymentsPanel';
import { panelActions, panelBody, type PanelAction } from './panel-actions';
import { useConfirm } from '@/components/ui/use-confirm';

interface InvoiceRow {
  id: string; invoice_number: string; invoice_date: string; due_date: string | null;
  currency: string; total: number; subtotal: number; tax_amount: number; status: string;
  confidence: number | null; supplier_name: string | null; vat_number: string | null;
  supplier_id: string | null; created_at: string; isActive?: boolean;
}

interface InvoiceLine {
  id: string; productId: string | null; supplierSku: string | null; description: string;
  quantity: number; unit: string; unitCost: number; taxRate: number; lineTotal: number;
  sku: string | null; productName: string | null;
}

interface InvoiceExtraction {
  supplierName?: string | null;
  vatNumber?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  currency?: string;
  confidence?: number;
  warnings?: string[];
  supplierCreated?: boolean;
  matchedLines?: number;
  unmatchedLines?: number;
  format?: string;
  extractionSource?: string | null;
  templateUsed?: boolean;
  templateSaved?: boolean;
  dictionaryMatched?: boolean;
}

interface InvoiceDetail extends InvoiceRow {
  /** Pedido al que está enlazada la factura: si lo hay, validar no mueve stock. */
  purchase_order_id: string | null;
  lines: InvoiceLine[];
  supplierVat: string | null;
  notes: string | null;
  document_url: string | null;
  extraction: InvoiceExtraction | null;
}

interface ImportResult {
  invoiceId: string;
  invoiceNumber: string;
  supplier: { id: string; name: string; vatNumber: string | null; created: boolean; provisional: boolean };
  lines: { total: number; matched: number; unmatched: number; pending: string[] };
  confidence: number;
  warnings: string[];
  documentId: string;
  format?: string;
  extractionSource?: string | null;
  templateUsed?: boolean;
  templateSaved?: boolean;
  dictionaryMatched?: boolean;
}

interface Supplier { id: string; name: string; vat_number: string | null }
interface ProductOption { id: string; sku: string; name: string; cost: number; base_unit: string }

interface LineDraft { productId: string; description: string; quantity: string; unitCost: string; taxRate: string }

const TODAY = new Date().toISOString().slice(0, 10);
const emptyLine = (): LineDraft => ({ productId: '', description: '', quantity: '1', unitCost: '0', taxRate: '21' });

export function InvoicesPage() {
  const qc = useQueryClient();
  const { can, user } = useAuth();
  const isAdmin = Boolean(user?.roles.some((role) => role.code === 'admin'));
  const { confirm, dialog: dialogConfirmacion } = useConfirm();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [newOpen, setNewOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [templateInvoice, setTemplateInvoice] = useState<{ invoiceId: string; supplierId: string; supplierName: string } | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (debounced) params.set('q', debounced);
  if (status) params.set('status', status);

  const invoicesQuery = useQuery({
    queryKey: ['invoices', page, debounced, status],
    queryFn: () => api<Paginated<InvoiceRow>>(`/purchase-invoices?${params.toString()}`)
  });

  const suppliersQuery = useQuery({ queryKey: ['suppliers', 'options'], queryFn: () => api<{ data: Supplier[] }>('/suppliers/options') });
  const productsQuery = useQuery({ queryKey: ['products-options'], queryFn: () => api<Paginated<ProductOption>>('/products?pageSize=200&active=true') });

  // Asociar a mano una línea que la importación dejó «Sin asociar». No mueve stock: el stock solo
  // entra al validar.
  const asignarLinea = useMutation({
    mutationFn: ({ invoiceId, lineId, productId }: { invoiceId: string; lineId: string; productId: string }) =>
      api(`/purchase-invoices/${invoiceId}/lines/${lineId}`, { method: 'PATCH', body: { productId } }),
    onSuccess: async () => {
      toast.success('Línea asociada');
      // Las claves de esta pantalla son ['invoice', id] y ['invoices', …]: invalidar otras no
      // recargaba nada y la línea seguía enseñando «Sin asociar».
      await qc.invalidateQueries({ queryKey: ['invoice'] });
      await qc.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: unknown) => toast.error(errorMessage(error))
  });

  const detailQuery = useQuery({
    queryKey: ['invoice', detailId],
    queryFn: () => api<InvoiceDetail>(`/purchase-invoices/${detailId}`),
    enabled: Boolean(detailId)
  });

  useEffect(() => { setPage(1); }, [debounced, status]);

  const importPdf = async (file: File) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await api<ImportResult>('/purchase-invoices/import', { method: 'POST', formData });
      toast.success(
        `Factura ${result.invoiceNumber} importada · ${result.lines.matched}/${result.lines.total} líneas asociadas` +
        (result.supplier.created ? ` · proveedor "${result.supplier.name}" creado` : '') +
        (result.templateSaved ? ' · plantilla PDF guardada' : result.templateUsed ? ' · plantilla PDF aplicada' : '')
      );
      if (result.warnings.length) toast(`Revisión: ${result.warnings.slice(0, 2).join(' · ')}`);
      await qc.invalidateQueries({ queryKey: ['invoices'] });
      await qc.invalidateQueries({ queryKey: ['suppliers'] });
      await qc.invalidateQueries({ queryKey: ['products-options'] });
      setDetailId(result.invoiceId);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const onPickFile = (file: File | null | undefined) => {
    if (file) void importPdf(file);
  };

  const archiveInvoice = async (invoice: { id: string; invoice_number: string }) => {
    if (!isAdmin) return;
    if (!(await confirm({
      title: `Archivar la factura ${invoice.invoice_number}`,
      description: 'Desaparece del listado, pero se conservan sus líneas, su recepción y los movimientos de stock.',
      confirmLabel: 'Archivar'
    }))) return;
    try {
      await api(`/purchase-invoices/${invoice.id}`, { method: 'DELETE' });
      toast.success('Factura archivada');
      setDetailId(null);
      await qc.invalidateQueries({ queryKey: ['invoices'] });
      await qc.invalidateQueries({ queryKey: ['invoice'] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const purgeInvoice = async (invoice: { id: string; invoice_number: string }) => {
    if (!isAdmin) return;
    if (!(await confirm({
      title: `Eliminar definitivamente la factura ${invoice.invoice_number}`,
      description: 'No se puede deshacer. Solo se permite si no está validada ni tiene recepciones.',
      confirmLabel: 'Eliminar definitivamente', destructive: true
    }))) return;
    try {
      await api(`/purchase-invoices/${invoice.id}?permanent=true`, { method: 'DELETE' });
      toast.success('Factura eliminada definitivamente');
      setDetailId(null);
      await qc.invalidateQueries({ queryKey: ['invoices'] });
      await qc.invalidateQueries({ queryKey: ['invoice'] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  /**
   * Cuando la factura puede ser de un pedido, el servidor no adivina: responde 409 con los
   * candidatos y aquí se elige enlazar o confirmar que es compra directa.
   */
  const [orderChoice, setOrderChoice] = useState<{
    invoiceId: string;
    number: string;
    /** Pedido con el que se intentó enlazar. Hace falta para reintentar sin perderlo. */
    purchaseOrderId: string | null;
    code: 'INVOICE_ORDER_CANDIDATES' | 'INVOICE_UNORDERED_LINES';
    candidates: { id: string; number: string; status: string; pending: number; unit: string | null }[];
  } | null>(null);

  const validate = async (id: string, opts?: {
    directPurchase?: boolean; allowUnordered?: boolean; purchaseOrderId?: string;
  }, numeroFactura?: string) => {
    try {
      const result = await api<{ mode: string; receiptNumber: string | null; purchaseOrderNumber: string | null }>(
        `/purchase-invoices/${id}/validate`, { method: 'POST', body: opts ?? {} }
      );
      // En una factura de pedido no hay recepción: el material ya entró al recibir.
      toast.success(result.mode === 'order'
        ? `Factura validada contra el pedido ${result.purchaseOrderNumber ?? ''} · el stock no se toca`
        : `Factura validada · recepción ${result.receiptNumber ?? ''}`);
      setOrderChoice(null);
      setDetailId(null);
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['invoice'] });
    } catch (error) {
      if (error instanceof ApiError
        && (error.code === 'INVOICE_ORDER_CANDIDATES' || error.code === 'INVOICE_UNORDERED_LINES')) {
        const details = (error.details ?? {}) as {
          candidates?: { id: string; number: string; status: string; pending: number; unit: string | null }[];
        };
        // El panel vive en el listado: si la validación salió de la ficha, hay que volver.
        setDetailId(null);
        setOrderChoice({
          invoiceId: id,
          number: numeroFactura ?? detailQuery.data?.invoice_number ?? '',
          purchaseOrderId: opts?.purchaseOrderId ?? null,
          code: error.code as 'INVOICE_ORDER_CANDIDATES' | 'INVOICE_UNORDERED_LINES',
          candidates: details.candidates ?? []
        });
        return;
      }
      toast.error(errorMessage(error));
    }
  };

  /**
   * Una sola petición: el servidor enlaza y valida en la misma transacción. Así, si la
   * validación falla (por ejemplo, la factura dice más de lo recibido), la factura **no**
   * queda enlazada a medias.
   */
  const linkAndValidate = async (invoiceId: string, purchaseOrderId: string, numeroFactura?: string) => {
    await validate(invoiceId, { purchaseOrderId }, numeroFactura);
  };

  const columns: Column<InvoiceRow>[] = [
    {
      key: 'number',
      header: 'Factura',
      cell: (row) => (
        <div>
          <p className="font-mono text-xs font-medium">{row.invoice_number}</p>
          <p className="text-[10px] text-muted-foreground">{row.supplier_name ?? 'Sin proveedor'}{row.vat_number ? ` · ${row.vat_number}` : ''}</p>
        </div>
      )
    },
    { key: 'date', header: 'Fecha', cell: (row) => <span className="text-[11px]">{formatDate(row.invoice_date)}</span> },
    { key: 'due', header: 'Vencimiento', cell: (row) => <span className="text-[11px] text-muted-foreground">{formatDate(row.due_date)}</span> },
    { key: 'total', header: 'Total', cell: (row) => <span className="font-medium tabular-nums">{formatMoney(row.total)}</span> },
    { key: 'status', header: 'Estado', cell: (row) => <StatusBadge status={row.status} /> },
    { key: 'confidence', header: 'Confianza', cell: (row) => row.confidence == null ? <Badge tone="neutral">Manual</Badge> : <Badge tone={row.confidence > 0.9 ? 'success' : 'warning'}>{Math.round(row.confidence * 100)}%</Badge> },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDetailId(row.id); }}>Ver</Button>
          {can('purchases.validate') && row.status === 'pending_review' ? (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); void validate(row.id, undefined, row.invoice_number); }}><CheckCircle2 /> Validar</Button>
          ) : null}
          {isAdmin ? (
            <>
              <Button variant="ghost" size="icon-sm" title="Archivar factura (conserva historial)" onClick={(e) => { e.stopPropagation(); void archiveInvoice(row); }}><Archive /></Button>
              <Button variant="ghost" size="icon-sm" className="text-destructive hover:text-destructive" title="Eliminar definitivamente (solo sin recepciones)" onClick={(e) => { e.stopPropagation(); void purgeInvoice(row); }}><Trash2 /></Button>
            </>
          ) : null}
        </div>
      )
    }
  ];

  if (templateInvoice) {
    return (
      <InvoiceTemplateEditor
        invoiceId={templateInvoice.invoiceId}
        supplierId={templateInvoice.supplierId}
        supplierName={templateInvoice.supplierName}
        onClose={() => setTemplateInvoice(null)}
        onSaved={() => {
          setTemplateInvoice(null);
          void qc.invalidateQueries({ queryKey: ['invoice'] });
          void qc.invalidateQueries({ queryKey: ['suppliers'] });
        }}
      />
    );
  }

  return (
    <div>
      {dialogConfirmacion}
      <div className={newOpen || detailId ? 'hidden' : ''}>
      {orderChoice ? (
        <Card className="mb-4 border-warning/60 p-4">
          <p className="text-sm font-medium">
            {orderChoice.number ? `Factura ${orderChoice.number} · ` : ''}
            {orderChoice.code === 'INVOICE_ORDER_CANDIDATES'
              ? 'Este material coincide con un pedido de compra'
              : 'Hay líneas que no son de este pedido'}
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground">
            {orderChoice.code === 'INVOICE_ORDER_CANDIDATES'
              ? 'Si la factura es de ese pedido, enlázala: el material ya entró al recibirlo y el stock no se vuelve a mover. Si es una compra suelta, confírmalo.'
              : 'Estas líneas van a entrar en stock como compra directa y el pedido seguirá con su saldo. Confírmalo solo si de verdad no son de este pedido; si sospechas que sí lo son, cancela y revisa la factura.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {orderChoice.candidates.map((candidate) => (
              <Button key={candidate.id} variant="outline" onClick={() => void linkAndValidate(orderChoice.invoiceId, candidate.id, orderChoice.number)}>
                Enlazar con {candidate.number} · {candidate.pending} {candidate.unit ?? 'ud'} pendiente{candidate.pending === 1 ? '' : 's'}
              </Button>
            ))}
            {/* Qué botones salen lo decide `panelActions` (con test): ofrecer compra directa
                con la factura ya enlazada duplicaría el stock de una línea ya recibida. */}
            {/* «Enlazar» ya lo dibujan los botones de candidato de arriba. */}
            {panelActions(orderChoice)
              .filter((action) => action !== 'cancel' && action !== 'link')
              .map((action) => (
              <Button
                key={action}
                variant={action === 'confirmUnordered' ? 'default' : 'outline'}
                onClick={() => void validate(orderChoice.invoiceId, panelBody(orderChoice, action as PanelAction), orderChoice.number)}
              >
                {action === 'directPurchase' ? 'Es compra directa' : 'Confirmar mercancía no pedida'}
              </Button>
            ))}
            <Button variant="ghost" onClick={() => setOrderChoice(null)}>Cancelar</Button>
          </div>
        </Card>
      ) : null}

      <PageHeader
        title="Facturas de compra"
        description="Sube el PDF o XML Facturae/UBL: se extraen los datos y el proveedor se crea si no existe. Nada entra en stock hasta validar."
        actions={can('purchases.write') ? (
          <>
            <Button variant="outline" loading={importing} onClick={() => importInputRef.current?.click()}>
              <Upload /> Importar factura
            </Button>
            <Button onClick={() => setNewOpen(true)}><Plus /> Nueva factura</Button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/pdf,.pdf,application/xml,text/xml,.xml"
              className="hidden"
              onChange={(event) => onPickFile(event.target.files?.[0])}
            />
          </>
        ) : null}
      />

      {can('purchases.write') ? (
        <Card className={`mb-4 border-dashed ${dragging ? 'border-primary bg-primary/5' : ''}`}>
          <div
            className="flex flex-wrap items-center gap-3 p-4"
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              onPickFile(event.dataTransfer.files?.[0]);
            }}
          >
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Upload className="size-5" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Procesar factura desde PDF o XML</p>
              <p className="text-xs text-muted-foreground">Arrastra el PDF o XML Facturae/UBL aquí o pulsa «Subir factura». Se extraen proveedor, número, fecha, líneas e importes; si el proveedor no existe se crea como provisional. En PDFs recurrentes se guarda una plantilla por proveedor.</p>
            </div>
            <Button loading={importing} onClick={() => importInputRef.current?.click()}>
              <Upload /> {importing ? 'Procesando…' : 'Subir factura'}
            </Button>
          </div>
        </Card>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar factura o proveedor…" className="w-full sm:w-72" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-40">
            <option value="">Todos los estados</option>
            <option value="pending_review">Pendientes de revisión</option>
            <option value="validated">Validadas</option>
            <option value="rejected">Rechazadas</option>
          </Select>
          <div className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground"><FileText className="size-3" /> {invoicesQuery.data?.meta.total ?? 0} facturas</div>
        </div>
        {invoicesQuery.error ? <ErrorState message={(invoicesQuery.error as Error).message} onRetry={() => void invoicesQuery.refetch()} /> : (
          <DataTable
            columns={columns} rows={invoicesQuery.data?.data ?? []} loading={invoicesQuery.isLoading}
            rowKey={(row) => row.id} onRowClick={(row) => setDetailId(row.id)}
            empty={<EmptyState title="Sin facturas" description="Registra una factura para dar entrada a materiales." icon={FileText} />}
            footer={<Pagination page={page} totalPages={invoicesQuery.data?.meta.totalPages ?? 1} total={invoicesQuery.data?.meta.total ?? 0} onPageChange={setPage} />}
          />
        )}
      </Card>
      </div>

      <NewInvoiceModal
        open={newOpen}
        onOpenChange={setNewOpen}
        suppliers={suppliersQuery.data?.data ?? []}
        products={productsQuery.data?.data ?? []}
        onSaved={() => { setNewOpen(false); void qc.invalidateQueries({ queryKey: ['invoices'] }); }}
      />

      <InlineView
        open={Boolean(detailId)} onOpenChange={(open) => { if (!open) setDetailId(null); }}
        title={detailQuery.data ? `Factura ${detailQuery.data.invoice_number}` : 'Factura'}
        description={detailQuery.data ? `${detailQuery.data.supplier_name ?? ''} · ${formatDate(detailQuery.data.invoice_date)}` : undefined}
        size="lg"
        footer={detailQuery.data ? (
          <>
            {detailQuery.data.document_url ? (
              <Button variant="outline" onClick={() => void openPdf(detailQuery.data!.document_url!)}><FileText /> Ver documento original</Button>
            ) : null}
            {detailQuery.data.status === 'pending_review' && can('purchases.validate') ? (
              <Button onClick={() => void validate(detailQuery.data!.id, undefined, detailQuery.data!.invoice_number)}><CheckCircle2 /> {(detailQuery.data?.purchase_order_id ? 'Validar factura' : 'Validar y recibir')}</Button>
            ) : null}
            {can('suppliers.write') && detailQuery.data.supplier_id && detailQuery.data.document_url && detailQuery.data.extraction?.format !== 'xml' ? (
              <Button variant="outline" onClick={() => setTemplateInvoice({
                invoiceId: detailQuery.data!.id,
                supplierId: detailQuery.data!.supplier_id!,
                supplierName: detailQuery.data!.supplier_name ?? 'Proveedor'
              })}>Editar plantilla</Button>
            ) : null}
            {isAdmin ? (
              <>
                <Button variant="outline" onClick={() => void archiveInvoice(detailQuery.data!)}><Archive /> Archivar</Button>
                <Button variant="destructive" onClick={() => void purgeInvoice(detailQuery.data!)}><Trash2 /> Eliminar definitivamente</Button>
              </>
            ) : null}
          </>
        ) : null}
      >
        {detailQuery.isLoading || !detailQuery.data ? <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded bg-muted" />)}</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-muted/20 p-3 text-xs sm:grid-cols-4">
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Estado</p><div className="mt-1"><StatusBadge status={detailQuery.data.status} /></div></div>
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Base</p><p className="mt-1 font-medium">{formatMoney(detailQuery.data.subtotal)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">IVA</p><p className="mt-1 font-medium">{formatMoney(detailQuery.data.tax_amount)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p><p className="mt-1 font-semibold">{formatMoney(detailQuery.data.total)}</p></div>
            </div>
            {can('finance.read') ? (
              <PaymentsPanel invoiceId={detailQuery.data.id} total={detailQuery.data.total} />
            ) : null}
            {detailQuery.data.extraction ? (
              <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <AlertTriangle className="size-4 text-amber-500" />
                  <p className="font-semibold">Importación automática</p>
                  <Badge tone={(detailQuery.data.confidence ?? 0) >= 0.8 ? 'success' : 'warning'}>
                    Confianza {Math.round((detailQuery.data.confidence ?? 0) * 100)}%
                  </Badge>
                  {detailQuery.data.extraction.supplierCreated ? <Badge tone="info">Proveedor creado</Badge> : null}
                  {detailQuery.data.extraction.format === 'xml' ? <Badge tone="success">XML Facturae/UBL</Badge> : null}
                  {detailQuery.data.extraction.dictionaryMatched ? <Badge tone="accent">Diccionario</Badge> : null}
                  {detailQuery.data.extraction.templateUsed ? <Badge tone="accent">Plantilla del proveedor</Badge> : null}
                  {detailQuery.data.extraction.templateSaved ? <Badge tone="success">Plantilla guardada</Badge> : null}
                  <span className="text-muted-foreground">
                    {detailQuery.data.extraction.matchedLines ?? 0} líneas asociadas · {detailQuery.data.extraction.unmatchedLines ?? 0} pendientes de asociar
                  </span>
                </div>
                {(detailQuery.data.extraction.warnings ?? []).length > 0 ? (
                  <ul className="mt-2 list-inside list-disc space-y-0.5 text-[11px] text-amber-700 dark:text-amber-300">
                    {(detailQuery.data.extraction.warnings ?? []).map((warning) => <li key={warning}>{warning}</li>)}
                  </ul>
                ) : (
                  <p className="mt-1 text-[11px] text-muted-foreground">Todos los datos principales se han reconocido correctamente.</p>
                )}
              </div>
            ) : null}
            <table className="table-dense w-full rounded border border-border">
              <thead className="bg-muted/30"><tr><th>Descripción</th><th>Artículo</th><th className="text-right">Cantidad</th><th className="text-right">Coste</th><th className="text-right">Importe</th></tr></thead>
              <tbody>
                {detailQuery.data.lines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.description}</td>
                    <td>
                      {line.sku ? (
                        <div><p className="font-mono text-[10px]">{line.sku}</p><p className="text-[10px] text-muted-foreground">{line.productName}</p></div>
                      ) : detailQuery.data.status !== 'pending_review' || detailQuery.data.isActive === false || !can('purchases.write') ? (
                        <Badge tone="warning">Sin asociar</Badge>
                      ) : (
                        <Select
                          aria-label={`Artículo de la línea ${line.description}`}
                          className="w-56"
                          value=""
                          disabled={asignarLinea.isPending}
                          onChange={(event) => {
                            const productId = event.target.value;
                            if (productId) {
                              asignarLinea.mutate({ invoiceId: detailQuery.data.id, lineId: line.id, productId });
                            }
                          }}
                        >
                          <option value="">Sin asociar · elige artículo…</option>
                          {(productsQuery.data?.data ?? []).map((product) => (
                            <option key={product.id} value={product.id}>{product.sku} · {product.name}</option>
                          ))}
                        </Select>
                      )}
                    </td>
                    <td className="text-right tabular-nums">{line.quantity} {line.unit}</td>
                    <td className="text-right tabular-nums">{formatMoney(line.unitCost)}</td>
                    <td className="text-right tabular-nums">{formatMoney(line.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {detailQuery.data.status === 'validated' ? <p className="text-xs text-emerald-600">Factura validada · stock actualizado</p> : null}
          </div>
        )}
      </InlineView>
    </div>
  );
}

function NewInvoiceModal({ open, onOpenChange, suppliers, products, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void;
  suppliers: Supplier[]; products: ProductOption[]; onSaved: () => void;
}) {
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(TODAY);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSupplierId(''); setInvoiceNumber(''); setInvoiceDate(TODAY); setDueDate('');
    setNotes(''); setLines([emptyLine()]); setDocumentId(null); setError(null);
  }, [open]);

  const totals = useMemo(() => {
    let subtotal = 0; let tax = 0;
    for (const line of lines) {
      const net = (Number(line.quantity) || 0) * (Number(line.unitCost) || 0);
      subtotal += net;
      tax += net * ((Number(line.taxRate) || 0) / 100);
    }
    return { subtotal, tax, total: subtotal + tax };
  }, [lines]);

  const updateLine = (index: number, key: keyof LineDraft, value: string) => {
    setLines((current) => current.map((line, i) => i === index ? { ...line, [key]: value } : line));
  };

  const selectProduct = (index: number, productId: string) => {
    const product = products.find((item) => item.id === productId);
    setLines((current) => current.map((line, i) => i === index ? {
      ...line, productId, description: product ? product.name : line.description,
      unitCost: product ? String(product.cost) : line.unitCost
    } : line));
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await api<{ documentId: string }>('/purchase-invoices/upload', { method: 'POST', formData });
      setDocumentId(result.documentId);
      toast.success('Documento archivado para revisión');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!supplierId) throw new Error('Selecciona un proveedor');
      if (!invoiceNumber.trim()) throw new Error('Indica el número de factura');
      const validLines = lines.filter((line) => line.description.trim() && Number(line.quantity) > 0);
      if (!validLines.length) throw new Error('Añade al menos una línea válida');
      await api('/purchase-invoices', {
        method: 'POST',
        body: {
          supplierId, invoiceNumber: invoiceNumber.trim(), invoiceDate,
          dueDate: dueDate || null, notes: notes || null, documentId,
          lines: validLines.map((line) => ({
            productId: line.productId || null, description: line.description,
            quantity: Number(line.quantity), unitCost: Number(line.unitCost),
            taxRate: Number(line.taxRate), supplierSku: null
          }))
        }
      });
      toast.success('Factura registrada para revisión');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="xl"
      title="Nueva factura de compra"
      description="Revisa los datos extraídos o introduce las líneas manualmente. Nada entra en stock hasta validar."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()}>Registrar factura</Button></>}
    >
      <div className="space-y-4">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Proveedor" required className="sm:col-span-2">
            <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">Selecciona…</option>
              {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}{supplier.vat_number ? ` · ${supplier.vat_number}` : ''}</option>)}
            </Select>
          </Field>
          <Field label="Nº factura" required><Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} /></Field>
          <Field label="Fecha" required><Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} /></Field>
          <Field label="Vencimiento"><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
          <Field label="Documento" className="sm:col-span-2">
            <div className="flex items-center gap-2">
              <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-input bg-card px-3 text-xs hover:bg-muted">
                <Upload className="size-3.5" /> {uploading ? 'Subiendo…' : documentId ? 'Documento adjunto' : 'Adjuntar PDF/imagen'}
                <input type="file" className="hidden" accept="application/pdf,image/png,image/jpeg" onChange={(e) => { const file = e.target.files?.[0]; if (file) void upload(file); }} />
              </label>
              {documentId ? <Badge tone="success">Adjuntado</Badge> : null}
            </div>
          </Field>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold">Líneas de factura</p>
            <Button variant="outline" size="sm" onClick={() => setLines((current) => [...current, emptyLine()])}><Plus /> Añadir línea</Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="table-dense w-full min-w-[760px]">
              <thead className="bg-muted/30"><tr><th className="min-w-52">Artículo</th><th>Descripción</th><th className="w-24">Cantidad</th><th className="w-28">Coste ud.</th><th className="w-20">IVA %</th><th className="w-24 text-right">Importe</th><th></th></tr></thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index}>
                    <td>
                      <Select value={line.productId} onChange={(e) => selectProduct(index, e.target.value)} className="h-8 text-xs">
                        <option value="">Sin asociar</option>
                        {products.map((product) => <option key={product.id} value={product.id}>{product.sku} · {product.name}</option>)}
                      </Select>
                    </td>
                    <td><Input value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} className="h-8 text-xs" placeholder="Descripción" /></td>
                    <td><Input type="number" step="0.001" value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} className="h-8 text-xs" /></td>
                    <td><Input type="number" step="0.0001" value={line.unitCost} onChange={(e) => updateLine(index, 'unitCost', e.target.value)} className="h-8 text-xs" /></td>
                    <td><Input type="number" step="0.01" value={line.taxRate} onChange={(e) => updateLine(index, 'taxRate', e.target.value)} className="h-8 text-xs" /></td>
                    <td className="text-right tabular-nums">{formatMoney((Number(line.quantity) || 0) * (Number(line.unitCost) || 0))}</td>
                    <td><Button variant="ghost" size="icon-sm" onClick={() => setLines((current) => current.length > 1 ? current.filter((_, i) => i !== index) : current)}><Trash2 /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end">
          <div className="w-64 space-y-1 rounded-lg border border-border bg-muted/20 p-3 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Base imponible</span><span>{formatMoney(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span>{formatMoney(totals.tax)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Total</span><span>{formatMoney(totals.total)}</span></div>
          </div>
        </div>

        <Field label="Notas"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></Field>
      </div>
    </InlineView>
  );
}
